// solicitacoesData.ts
import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../../../context/auth-context";

const CASES_SOLICITANTE_URL =
  "https://api.eva-googlemarine.com/departamentos/laboratorio/acompanhamento/get-lista-cases-laboratorio-solicitante";

const ACCESS_TOKEN_KEY = "access_token";

function na(v: any): string {
  if (v === null || v === undefined) return "N/A";
  const s = String(v).trim();
  return s === "" ? "N/A" : s;
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

type SolicitanteAPI = {
  id?: string | number | null;
  nome?: string | null;
};

export type CaseSolicitanteAPI = {
  case_ref: string | null;
  public_token?: string | null;

  equipamento_nome?: string | null;
  descricao_problema?: string | null;
  prioridade?: string | number | null;

  nf?: string | null;
  embarcacao_cliente_id?: string | number | null;
  embarcacao_nome?: string | null;

  empresa_cliente_id?: string | number | null;
  empresa_razao_social?: string | null;

  status_atual_id?: string | number | null;
  nomenclatura_status_atual?: string | null;
  status_atual_nome?: string | null;

  equipamento_coletado?: boolean | null;
  equipamento_coleta_status?: string | null;

  solicitantes?: SolicitanteAPI[] | null;

  created_at?: string | null;
  updated_at?: string | null;
};

type CasesSolicitanteResp = {
  fonte?: "cache" | "banco";
  solicitante_id?: number | string;
  total?: number;
  data: CaseSolicitanteAPI[];
  erro?: string;
};

export type CaseSolicitante = {
  caseRef: string;
  publicToken: string;

  equipamentoNome: string;
  descricaoProblema: string;
  prioridade: string;

  nf: string;
  embarcacaoId: string | number | null;
  embarcacaoNome: string;

  empresaRazaoSocial: string;
  empresaClienteId?: string | number | null;

  statusAtualId?: string | number | null;
  statusAtualNome: string;

  equipamentoColetado: boolean;
  equipamentoColetaStatus: string;

  solicitantes: Array<{
    id: string | number | null;
    nome: string;
  }>;

  solicitantePrincipalNome: string;
  copiadosNomes: string[];

  createdAt?: string | null;
  updatedAt?: string | null;
};

function normalizeSolicitantes(
  r: CaseSolicitanteAPI
): Array<{ id: string | number | null; nome: string }> {
  const arr = Array.isArray(r?.solicitantes) ? r.solicitantes : [];

  return arr
    .map((s) => ({
      id: s?.id ?? null,
      nome: na(s?.nome),
    }))
    .filter((s) => s.nome !== "N/A");
}

function normalizeCaseSolicitante(r: CaseSolicitanteAPI): CaseSolicitante {
  const solicitantes = normalizeSolicitantes(r);
  const principal = solicitantes[0] || null;
  const copiadosNomes = solicitantes.slice(1).map((x) => x.nome);

  const equipamentoColetado = Boolean(r.equipamento_coletado);
  const equipamentoColetaStatus =
    na(r.equipamento_coleta_status) !== "N/A"
      ? na(r.equipamento_coleta_status)
      : equipamentoColetado
      ? "Coletado"
      : "Pendente coleta";

  return {
    caseRef: na(r.case_ref),
    publicToken: na(r.public_token),

    equipamentoNome: na(r.equipamento_nome),
    descricaoProblema: na(r.descricao_problema),
    prioridade: na(r.prioridade),

    nf: na(r.nf),
    embarcacaoId: r.embarcacao_cliente_id ?? null,
    embarcacaoNome: na(r.embarcacao_nome),

    empresaRazaoSocial: na(r.empresa_razao_social),
    empresaClienteId: r.empresa_cliente_id ?? null,

    statusAtualId: r.status_atual_id ?? null,
    statusAtualNome: na(r.nomenclatura_status_atual ?? r.status_atual_nome),

    equipamentoColetado,
    equipamentoColetaStatus,

    solicitantes,
    solicitantePrincipalNome: principal?.nome ?? "N/A",
    copiadosNomes,

    createdAt: r.created_at ?? null,
    updatedAt: r.updated_at ?? null,
  };
}

async function getListaCasesSolicitante(
  solicitanteId: string | number
): Promise<{
  fonte: "cache" | "banco" | undefined;
  data: CaseSolicitante[];
}> {
  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const url = `${CASES_SOLICITANTE_URL}?solicitante_id=${encodeURIComponent(
    String(solicitanteId)
  )}`;

  const res = await fetch(url, {
    method: "GET",
    headers,
    cache: "no-store",
    credentials: "include",
  });

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;
  const json = (await res
    .json()
    .catch(() => null)) as CasesSolicitanteResp | null;

  if (!res.ok) {
    const msg = json?.erro || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }

  const rows = Array.isArray(json?.data) ? json!.data : [];
  return { fonte, data: rows.map(normalizeCaseSolicitante) };
}

export function useListaCasesLaboratorioSolicitante() {
  const { sessao, loading: authLoading } = useAuth();

  const [itens, setItens] = useState<CaseSolicitante[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [erro, setErro] = useState<string | null>(null);

  const solicitanteId = sessao?.solicitante_id ?? null;
  const inFlightRef = useRef(false);

  const refresh = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (authLoading) return;

      if (!solicitanteId) {
        setItens([]);
        setFonte(undefined);
        setErro("Sessão sem solicitante_id (usuário não é solicitante).");
        setLoading(false);
        return;
      }

      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const silent = Boolean(opts?.silent);
      if (!silent) setLoading(true);
      setErro(null);

      try {
        const resp = await getListaCasesSolicitante(solicitanteId);
        setItens(resp.data);
        setFonte(resp.fonte);
      } catch (e: any) {
        setItens([]);
        setFonte(undefined);
        setErro(e?.message || "Falha ao carregar cases do solicitante.");
      } finally {
        inFlightRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    [authLoading, solicitanteId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (authLoading) return;
    if (!solicitanteId) return;

    const POLL_MS = 5000;

    let disposed = false;
    const timer = setInterval(() => {
      if (disposed) return;
      if (document.visibilityState !== "visible") return;
      refresh({ silent: true }).catch(() => null);
    }, POLL_MS);

    const onVis = () => {
      if (document.visibilityState === "visible") {
        refresh({ silent: true }).catch(() => null);
      }
    };

    document.addEventListener("visibilitychange", onVis);

    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [authLoading, solicitanteId, refresh]);

  return {
    itens,
    loading,
    fonte,
    erro,
    refresh,
    solicitanteId,
    hasRoleSolicitante: Boolean(sessao?.roles?.includes("solicitante")),
  };
}

export function useListaEquipamentosReparo() {
  return useListaCasesLaboratorioSolicitante();
}
